import { api } from './client';
import type { FollowUpKind } from './follow-ups.api';
import { i18nMetaMap } from '@/lib/i18n-map';
import '@/lib/i18n-map';

export type AppointmentStatus =
  /** Asked for by the patient through the portal; not yet confirmed. */
  | 'pending'
  | 'scheduled'
  | 'confirmed'
  /** The patient phoned or was phoned: on the way, past the start. */
  | 'late'
  | 'arrived'
  | 'waiting'
  | 'treating'
  | 'done'
  | 'noshow'
  | 'urgent'
  | 'cancelled';

export interface LeaveBand {
  staffId: string;
  dateFrom: string;
  dateTo: string;
  kind: string;
}

export interface Appointment {
  id: string;
  patientId: string | null;
  patientName: string;
  phone: string | null;
  doctorId: string;
  doctorName: string | null;
  doctorColor: string | null;
  serviceId: string | null;
  service: string | null;
  emoji: string | null;
  apptDate: string;
  hour: number;
  min: number;
  dur: number;
  /** Minutes from midnight — what the calendar positions blocks with. */
  startMinutes: number;
  endMinutes: number;
  status: AppointmentStatus;
  price: number;
  /**
   * Plătitorul persoană juridică al unei vizite facturate fără procedură
   * (contabilitate §2.4); null = pacientul. Cu proceduri, fiecare rând are al lui.
   */
  payerContractId: string | null;
  /** Cât din preț plătește acel plătitor; null fără contract. */
  payerShare: number | null;
  paid: boolean;
  notes: string | null;
  seriesId: string | null;
  startedAt: string | null;
  branchId: string | null;
  deletedAt: string | null;
  createdAt: string;
  /** True once a completed 043/e record exists; the calendar marks the gap. */
  hasVisitRecord: boolean;
  noshowReason: string | null;
  /** Why it was cancelled — kept with „Anulat" and with a removal alike. */
  deleteReason: string | null;
  serviceColor: string | null;
  /** The patient's standing warning — allergy, anticoagulants, anxiety. */
  patientAlert: string | null;
  phaseId: string | null;
  phaseName: string | null;
  phaseTeeth: string | null;
  phaseStatus: string | null;
  /** Plan rows booked on it or done at it. */
  procedureCount: number;
  /**
   * Those rows, in plan order — the card and the panel list them one under
   * another. Only the calendar's list carries them; other answers send `[]`.
   */
  procedures: AppointmentProcedure[];
  /** Of those, how many nobody has confirmed as done or not done. */
  unconfirmed: number;
  /** Finished, unpaid procedure on the plan — reception should collect. */
  unpaidDone: boolean;
  /** Work was confirmed done at it. Only then is its 043/e owed. */
  workDone: boolean;
}

/** One plan row on a booking, as the calendar lists it. */
export interface AppointmentProcedure {
  id: string;
  name: string | null;
  teeth: string | null;
  cost: number | null;
  status: string;
}

/**
 * One entry of a booking's history: an audit row of the booking itself, or of
 * a procedure, the 043/e or a payment tied to it.
 */
export interface AppointmentHistoryEntry {
  id: string;
  at: string;
  /** `appointments`, `treatment_plans`, `clinical_visits` or `payments`. */
  table: string;
  recordId: string | null;
  action: string;
  actorName: string | null;
  /** On a procedure's entry: its name and teeth, whatever else changed. */
  subject: string | null;
  changes: Array<{ field: string; from: unknown; to: unknown }>;
}

/** A booking that overlaps a proposed one, returned with a 409. */
export interface Clash {
  id: string;
  patientName: string;
  from: string;
  to: string;
}

/** A plan row another booking still holds, returned with a 409 `RowsBooked`. */
export interface HeldRow {
  phaseId: string;
  phase: string;
  appointmentId: string;
  apptDate: string;
  time: string;
}

export interface CreateAppointmentInput {
  patientId?: string | null;
  patientName: string;
  phone?: string | null;
  doctorId: string;
  phaseId?: string | null;
  /** Plan rows this booking performs. On an edit, sent only when the ticks changed. */
  phaseIds?: string[];
  serviceId?: string | null;
  apptDate: string;
  hour: number;
  min: number;
  dur: number;
  status?: AppointmentStatus;
  service?: string | null;
  price?: number;
  /** Mirrors the DTO: null removes the payer; absent leaves it as it was. */
  payerContractId?: string | null;
  /** Absent with a new contract = the whole price. */
  payerShare?: number | null;
  notes?: string | null;
  repeatWeekly?: number;
  /** Câte săptămâni între vizitele seriei. 1 implicit. */
  repeatEvery?: number;
  /** Book over a clash the user has been shown and accepted. */
  force?: boolean;
  /** Move plan rows another booking holds, once the user has seen where. */
  takeRows?: boolean;
  /** Doar la o programare nouă: cererea de pacient nou din care s-a făcut. */
  inquiryId?: string;
  /**
   * Doar la o programare nouă, cu pacient: rândul listei de sunat din care s-a
   * făcut (plan neînceput, rechemare). Apelul se încheie cu „programat".
   */
  followUp?: { kind: FollowUpKind; planName?: string | null };
}

export interface Availability {
  slots: string[];
  busy: Array<{ from: string; to: string }>;
}

/** O zi din grila lunii: numărul zilei întregi, și cât încape pe ecran. */
export interface MonthDay {
  date: string;
  count: number;
  first: Appointment[];
}

/** O zi în care clinica nu lucrează — din zilele de lucru sau din sărbători. */
export interface ClosedDay {
  date: string;
  reason: string;
}

/** Orele clinicii într-o zi a săptămânii. */
export interface DayHours {
  start: string;
  end: string;
}

/**
 * Regulile calendarului, așa cum le aplică serverul.
 *
 * Pasul, durata implicită și „se poate programa peste" erau constante scrise
 * în trei locuri — în API, în grilă și în fereastră. Vin dintr-un singur loc
 * acum, ca ecranul să nu poată desena altă regulă decât cea aplicată.
 */
export interface CalendarRules {
  step: number;
  defaultDur: number;
  allowForce: boolean;
  hours: { base: DayHours; byWeekday: Partial<Record<number, DayHours>> };
  cancelReasons: string[];
  noshowReasons: string[];
}

/** O programare cerută din portal, neconfirmată (`GET /appointments/portal-requests`). */
export interface PortalRequest {
  id: string;
  date: string;
  hour: number;
  min: number;
  dur: number;
  patientId: string | null;
  patientName: string;
  phone: string | null;
  service: string | null;
  notes: string | null;
  doctorId: string;
  doctorName: string | null;
}

export const appointmentsApi = {
  /**
   * Find a patient's appointments anywhere in the diary.
   *
   * Separate from `list` because it is not bounded by a date range — that is
   * the whole point. The server caps it at twelve and refuses fewer than two
   * characters.
   */
  search: (q: string) => api.get<Appointment[]>('/appointments/search', { query: { q } }),

  list: (params: {
    from: string;
    to: string;
    doctorId?: string;
    patientId?: string;
    includeDeleted?: boolean;
  }) => api.get<Appointment[]>('/appointments', { query: params }),

  /**
   * Luna, așa cum o desenează grila: câte pe zi, și primele câteva nume.
   *
   * Grila arată trei pacienți pe zi. Cerând luna întreagă ca să-i deseneze, o
   * clinică cu 3.000 de programări într-o lună descărca **2,1 MB** din care
   * folosea 93 de rânduri.
   */
  month: (month: string) => api.get<MonthDay[]>('/appointments/month', { query: { month } }),

  availability: (doctorId: string, date: string, duration = 30) =>
    api.get<Availability>('/appointments/availability', { query: { doctorId, date, duration } }),

  /** The empty stretches in a day, per doctor — reception's call list. */
  /** Approved leave touching the range: a full-day band on the doctor's column. */
  leaves: (from: string, to: string) =>
    api.get<LeaveBand[]>('/appointments/leaves', { query: { from, to } }),
  gaps: (date: string, doctorId?: string) =>
    api.get<DayGaps[]>('/appointments/gaps', { query: { date, doctorId } }),

  create: (body: CreateAppointmentInput) => api.post<Appointment[]>('/appointments', body),

  /** Everything that happened to a booking: its own changes, its procedures, 043/e and payments. */
  history: (id: string) => api.get<AppointmentHistoryEntry[]>(`/appointments/${id}/history`),

  update: (id: string, body: Partial<CreateAppointmentInput> & { paid?: boolean }) =>
    api.patch<Appointment>(`/appointments/${id}`, body),

  /** Cererile din portal încă neconfirmate, de azi încolo — fila „Din portal" de pe Așteptare. */
  portalRequests: () => api.get<PortalRequest[]>('/appointments/portal-requests'),
  setStatus: (id: string, status: AppointmentStatus, reason?: string) =>
    api.patch<Appointment>(`/appointments/${id}/status`, { status, reason }),

  noshows: (patientId: string) =>
    api.get<
      Array<{
        id: string;
        date: string;
        time: string;
        reason: string | null;
        doctorName: string | null;
      }>
    >(`/appointments/patients/${patientId}/noshows`),

  cancel: (id: string, reason?: string) =>
    api.delete<void>(`/appointments/${id}`, { body: { reason: reason ?? null } }),

  restore: (id: string) => api.post<Appointment>(`/appointments/${id}/restore`),

  /** Pasul, durata implicită, orele clinicii, motivele — vezi `CalendarRules`. */
  rules: () => api.get<CalendarRules>('/appointments/rules'),
  /** Prima zi de după azi în care clinica lucrează, după program și zilele închise. */
  nextOpenDay: () => api.get<{ date: string }>('/appointments/next-open-day'),
  /** Anulările și neprezentările dintr-o perioadă, numărate pe motiv (CAL-10). */
  reasons: (from: string, to: string) =>
    api.get<{
      from: string;
      to: string;
      cancel: Array<{ reason: string; count: number }>;
      noshow: Array<{ reason: string; count: number }>;
    }>('/appointments/reasons', { query: { from, to } }),

  /** Zilele în care clinica nu lucrează, în intervalul cerut. */
  closedDays: (from: string, to: string) =>
    api.get<ClosedDay[]>('/appointments/closed-days', { query: { from, to } }),

  /**
   * Fișa pacientului pentru o programare care n-are una — și legarea ei.
   * `check`: întâi întreabă dacă pacientul are deja o fișă (409 cu candidații);
   * `linkTo`: leagă programarea de o fișă existentă în loc să facă una nouă.
   */
  createPatient: (id: string, options: { check?: boolean; linkTo?: string } = {}) =>
    api.post<Appointment>(`/appointments/${id}/patient`, undefined, {
      query: { check: options.check || undefined, linkTo: options.linkTo },
    }),
};

/** Status presentation, in one place so the calendar and lists agree. */
export interface DayGaps {
  doctorId: string;
  doctorName: string;
  color: string | null;
  workFrom: string;
  workTo: string;
  /** Minutes already taken, lunch included. */
  booked: number;
  gaps: Array<{ from: string; to: string; minutes: number }>;
}

export const STATUS_META = i18nMetaMap<
  AppointmentStatus,
  { label: string; tone: 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'brand'; icon: string }
>('common', {
  // The icons are the old system's, kept verbatim: reception reads them
  // faster than any label.
  pending: { label: 'cerutaDePacient', tone: 'warn', icon: '📨' },
  scheduled: { label: 'programat', tone: 'neutral', icon: '🗓️' },
  confirmed: { label: 'confirmat', tone: 'info', icon: '👍' },
  late: { label: 'intarzie', tone: 'warn', icon: '🏃' },
  arrived: { label: 'sosit', tone: 'brand', icon: '📍' },
  waiting: { label: 'asteptare', tone: 'warn', icon: '⏳' },
  treating: { label: 'inTratament', tone: 'brand', icon: '🦷' },
  done: { label: 'finalizat', tone: 'ok', icon: '🏁' },
  noshow: { label: 'neprezentat', tone: 'danger', icon: '❌' },
  urgent: { label: 'urgenta', tone: 'danger', icon: '🚨' },
  cancelled: { label: 'anulat', tone: 'danger', icon: '🚫' },
});

/** The transitions the front desk actually uses, in the order they happen. */
export const STATUS_FLOW: AppointmentStatus[] = [
  // A self-booking starts here; accepting it makes it an ordinary appointment.
  'pending',
  'scheduled',
  'confirmed',
  'late',
  'arrived',
  'waiting',
  'treating',
  'done',
];
